import type { ErrorTag, HekateDisplayForm } from '@onshape/www-inertia';
import { ErrorTagUtil } from '@onshape/www-inertia';
import WaUtil from './WaUtil';

type FormErrorMessages = Array<{
  message: string;
  hint?: string;
}>;

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export default class ErrorUtil {
  /** Send a mess to sentry or log it if not in sentry. */
  public static safePostMessageToSentry({
    tag,
    msg,
    error,
  }: {
    tag: ErrorTag;
    msg: string;
    error?: unknown;
  }) {
    WaUtil.waPush({
      event: 'PostSentryError',
      data: {
        tag,
        msg,
      },
    });

    if (!window.errorReporter) {
      console.error('Debug errorReporter message: ', `${tag} ${msg}`);
      return;
    }

    try {
      const fullError = `${tag} - ${msg}`;

      console.error(fullError);
      window.errorReporter.postToSentry({ tag, msg, error: error || undefined });
    } catch (e) {
      console.log(e, msg);
    }

    // Push to mouseflow to watch event. Not must use low level mouseflow object
    // instead of TrackingUtil to avoid dependency cycle
    try {
      const toPush = ['tag', `error-${tag}`];

      window._mfq = window._mfq || [];

      console.log('mouseFlowPush (error push)', toPush);

      window._mfq.push(toPush);
    } catch (e) {
      console.error(`Could not push to mouseflow ${e}`);
    }
  }

  // safePost and throw msg
  public static internalError({ msg, error }: { msg: string; error?: unknown }): Error {
    ErrorUtil.safePostMessageToSentry({
      tag: ErrorTagUtil.hekateInternalError,
      msg,
      error: error || undefined,
    });
    return new Error(msg);
  }

  /** Add uniq errors (and conditionally remove any generic errors) */
  public static addSpecificFormError({
    formDefinition,
    errorMessages,
  }: {
    formDefinition: Ref<HekateDisplayForm | null>;
    errorMessages: Array<{
      message: string;
      hint?: string;
    }>;
  }) {
    if (formDefinition.value === null) {
      return;
    }
    if (errorMessages.length === 0) {
      return;
    }
    const errors: FormErrorMessages = [];
    const curCleanErrors = formDefinition.value.errorMessages.filter(
      (ce) => ce.hint !== 'generic-error'
    );
    errorMessages.forEach((em) => {
      const found = curCleanErrors.find((cce) => cce.message === em.message);
      if (!found) return;
      errors.push(found);
    });

    formDefinition.value.errorMessages.splice(0, formDefinition.value.errorMessages.length);
    formDefinition.value.errorMessages.push(...errors);
  }
}
